import React, { useEffect, useMemo, useState } from "react";
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	Grid,
	IconButton,
	InputAdornment,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import RefreshIcon from "@mui/icons-material/Refresh";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import WorkHistoryIcon from "@mui/icons-material/WorkHistory";
import SearchIcon from "@mui/icons-material/Search";
import CancelIcon from "@mui/icons-material/Cancel";

import {
	createConvenio,
	deleteConvenio,
	getConvenios,
	updateConvenio,
} from "../../services/apis";

const emptyForm = {
	id: null,
	nombre: "",
	horas_convenio: "8.00",
	activo: 1,
};

const normalizeDecimalInput = (value) => {
	const normalized = String(value ?? "")
		.replace(",", ".")
		.trim();

	if (normalized === "") return "";

	return normalized;
};

const formatHoras = (value) => {
	const number = Number(value);

	if (!Number.isFinite(number)) {
		return "";
	}

	return number.toFixed(2);
};

const calcularEjemploSalida = (horaEntrada = "08:00", horasConvenio = 8) => {
	const horas = Number(horasConvenio);

	if (!Number.isFinite(horas) || horas <= 0) {
		return "";
	}

	const [hh, mm] = horaEntrada.split(":").map(Number);

	if (!Number.isFinite(hh) || !Number.isFinite(mm)) {
		return "";
	}

	const totalMinutosEntrada = hh * 60 + mm;
	const totalMinutosConvenio = Math.round(horas * 60);
	const total = totalMinutosEntrada + totalMinutosConvenio;

	const salidaHoras = Math.floor(total / 60) % 24;
	const salidaMinutos = total % 60;

	return `${String(salidaHoras).padStart(2, "0")}:${String(salidaMinutos).padStart(2, "0")}`;
};

export const Convenios = ({
	open = false,
	onClose = null,
	dialogMode = false,
	onConvenioSaved = null,
}) => {
	const isDialog = dialogMode || typeof onClose === "function";

	const [convenios, setConvenios] = useState([]);
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [deletingId, setDeletingId] = useState(null);

	const [error, setError] = useState("");
	const [success, setSuccess] = useState("");

	const [search, setSearch] = useState("");
	const [filtroActivo, setFiltroActivo] = useState("todos");

	const [formOpen, setFormOpen] = useState(false);
	const [form, setForm] = useState(emptyForm);
	const [formError, setFormError] = useState("");

	const shouldRenderContent = !isDialog || open;

	const cargarConvenios = async () => {
		setLoading(true);
		setError("");
		setSuccess("");

		try {
			const response = await getConvenios();

			if (response?.success) {
				setConvenios(Array.isArray(response.data) ? response.data : []);
			} else {
				setConvenios([]);
				setError(response?.message || "No se pudieron cargar los convenios");
			}
		} catch (err) {
			console.error("Error cargando convenios:", err);
			setConvenios([]);
			setError("No se pudieron cargar los convenios");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (!shouldRenderContent) return;

		cargarConvenios();
	}, [shouldRenderContent]);

	const conveniosFiltrados = useMemo(() => {
		const term = search.trim().toLowerCase();

		return convenios.filter((convenio) => {
			const activo = Number(convenio.activo ?? 1);

			if (filtroActivo === "activos" && activo !== 1) return false;
			if (filtroActivo === "inactivos" && activo === 1) return false;

			if (!term) return true;

			return [
				convenio.id,
				convenio.nombre,
				convenio.horas_convenio,
			]
				.join(" ")
				.toLowerCase()
				.includes(term);
		});
	}, [convenios, search, filtroActivo]);

	const resetForm = () => {
		setForm(emptyForm);
		setFormError("");
	};

	const handleNuevo = () => {
		resetForm();
		setFormOpen(true);
	};

	const handleEditar = (convenio) => {
		setForm({
			id: convenio.id ?? null,
			nombre: convenio.nombre ?? "",
			horas_convenio: formatHoras(convenio.horas_convenio ?? 8),
			activo: Number(convenio.activo ?? 1),
		});
		setFormError("");
		setFormOpen(true);
	};

	const handleCerrarForm = () => {
		if (saving) return;

		setFormOpen(false);
		resetForm();
	};

	const validarForm = () => {
		const nombre = form.nombre.trim();
		const horas = Number(normalizeDecimalInput(form.horas_convenio));

		if (!nombre) {
			return "El nombre del convenio es obligatorio";
		}

		if (!Number.isFinite(horas) || horas <= 0) {
			return "Las horas del convenio deben ser mayores que 0";
		}

		if (horas > 24) {
			return "Las horas del convenio no pueden ser superiores a 24";
		}

		return "";
	};

	const handleGuardar = async () => {
		const validationMessage = validarForm();

		if (validationMessage) {
			setFormError(validationMessage);
			return;
		}

		setSaving(true);
		setFormError("");
		setError("");
		setSuccess("");

		const payload = {
			nombre: form.nombre.trim(),
			horas_convenio: Number(normalizeDecimalInput(form.horas_convenio)),
			activo: Number(form.activo) === 1 ? 1 : 0,
		};

		try {
			const response = form.id
				? await updateConvenio(form.id, payload)
				: await createConvenio(payload);

			if (response?.success) {
				setSuccess(form.id ? "Convenio actualizado correctamente" : "Convenio creado correctamente");
				setFormOpen(false);
				resetForm();
				await cargarConvenios();

				if (typeof onConvenioSaved === "function") {
					onConvenioSaved(response.data || payload);
				}
			} else {
				setFormError(response?.message || "No se pudo guardar el convenio");
			}
		} catch (err) {
			console.error("Error guardando convenio:", err);
			setFormError("No se pudo guardar el convenio");
		} finally {
			setSaving(false);
		}
	};

	const handleToggleActivo = async (convenio) => {
		const id = Number(convenio.id);
		const nextActivo = Number(convenio.activo ?? 1) === 1 ? 0 : 1;

		if (!id) return;

		setSaving(true);
		setError("");
		setSuccess("");

		try {
			const response = await updateConvenio(id, {
				nombre: convenio.nombre,
				horas_convenio: Number(convenio.horas_convenio),
				activo: nextActivo,
			});

			if (response?.success) {
				setSuccess(nextActivo === 1 ? "Convenio activado" : "Convenio desactivado");
				await cargarConvenios();
			} else {
				setError(response?.message || "No se pudo actualizar el estado del convenio");
			}
		} catch (err) {
			console.error("Error cambiando estado del convenio:", err);
			setError("No se pudo actualizar el estado del convenio");
		} finally {
			setSaving(false);
		}
	};

	const handleEliminar = async (convenio) => {
		const id = Number(convenio.id);

		if (!id) return;

		const confirmado = window.confirm(
			`¿Seguro que quieres eliminar el convenio "${convenio.nombre}"?`
		);

		if (!confirmado) return;

		setDeletingId(id);
		setError("");
		setSuccess("");

		try {
			const response = await deleteConvenio(id);

			if (response?.success) {
				setSuccess("Convenio eliminado correctamente");
				await cargarConvenios();
			} else {
				setError(response?.message || "No se pudo eliminar el convenio");
			}
		} catch (err) {
			console.error("Error eliminando convenio:", err);
			setError("No se pudo eliminar el convenio. Puede que esté asignado a algún parte.");
		} finally {
			setDeletingId(null);
		}
	};

	const content = (
		<Box sx={{ p: isDialog ? 0 : 2 }}>
			<Paper elevation={2} sx={{ p: 2, mb: 2 }}>
				<Grid container spacing={2} alignItems="center">
					<Grid size={{ xs: 12, md: 5 }}>
						<Typography variant="h4" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
							<WorkHistoryIcon sx={{ fontSize: 38 }} color="primary" />
							Convenios
						</Typography>

						<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
							Gestiona las horas máximas por convenio. Estas horas se usarán para calcular la hora de salida inicial.
						</Typography>
					</Grid>

					<Grid size={{ xs: 12, md: 3 }}>
						<TextField
							fullWidth
							size="small"
							label="Buscar convenio"
							value={search}
							onChange={(event) => setSearch(event.target.value)}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<SearchIcon />
									</InputAdornment>
								),
							}}
						/>
					</Grid>

					<Grid size={{ xs: 12, md: 2 }}>
						<FormControl fullWidth size="small">
							<InputLabel id="filtro-activo-convenios-label">Estado</InputLabel>
							<Select
								labelId="filtro-activo-convenios-label"
								label="Estado"
								value={filtroActivo}
								onChange={(event) => setFiltroActivo(event.target.value)}
							>
								<MenuItem value="todos">Todos</MenuItem>
								<MenuItem value="activos">Activos</MenuItem>
								<MenuItem value="inactivos">Inactivos</MenuItem>
							</Select>
						</FormControl>
					</Grid>

					<Grid size={{ xs: 12, md: 2 }}>
						<Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
							<Tooltip title="Recargar">
								<span>
									<IconButton onClick={cargarConvenios} disabled={loading}>
										<RefreshIcon />
									</IconButton>
								</span>
							</Tooltip>

							<Button
								variant="contained"
								startIcon={<AddIcon />}
								onClick={handleNuevo}
							>
								Nuevo
							</Button>
						</Box>
					</Grid>
				</Grid>
			</Paper>

			{error && (
				<Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
					{error}
				</Alert>
			)}

			{success && (
				<Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
					{success}
				</Alert>
			)}

			<Paper elevation={2} sx={{ overflow: "hidden" }}>
				{loading ? (
					<Box
						sx={{
							minHeight: 300,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
						}}
					>
						<CircularProgress />
					</Box>
				) : (
					<Table stickyHeader size="small">
						<TableHead>
							<TableRow>
								<TableCell align="center" sx={{ fontSize: 18, fontWeight: "bold" }}>
									ID
								</TableCell>
								<TableCell sx={{ fontSize: 18, fontWeight: "bold" }}>
									Convenio
								</TableCell>
								<TableCell align="center" sx={{ fontSize: 18, fontWeight: "bold" }}>
									<AccessTimeIcon sx={{ mb: -0.7 }} color="primary" /> Horas
								</TableCell>
								<TableCell align="center" sx={{ fontSize: 18, fontWeight: "bold" }}>
									Ejemplo 08:00
								</TableCell>
								<TableCell align="center" sx={{ fontSize: 18, fontWeight: "bold" }}>
									Estado
								</TableCell>
								<TableCell align="center" sx={{ fontSize: 18, fontWeight: "bold" }}>
									Acciones
								</TableCell>
							</TableRow>
						</TableHead>

						<TableBody>
							{conveniosFiltrados.length === 0 && (
								<TableRow>
									<TableCell colSpan={6} align="center" sx={{ py: 4 }}>
										<Typography color="text.secondary">
											No hay convenios para mostrar
										</Typography>
									</TableCell>
								</TableRow>
							)}

							{conveniosFiltrados.map((convenio) => {
								const id = Number(convenio.id);
								const activo = Number(convenio.activo ?? 1);
								const horas = Number(convenio.horas_convenio ?? 0);
								const salidaEjemplo = calcularEjemploSalida("08:00", horas);

								return (
									<TableRow key={id} hover>
										<TableCell align="center" sx={{ fontSize: 16 }}>
											{id}
										</TableCell>

										<TableCell sx={{ fontSize: 16 }}>
											<Typography sx={{ fontWeight: 600 }}>
												{convenio.nombre}
											</Typography>
										</TableCell>

										<TableCell align="center" sx={{ fontSize: 16 }}>
											<Chip
												icon={<AccessTimeIcon />}
												label={`${formatHoras(horas)} h`}
												color="primary"
												variant="outlined"
											/>
										</TableCell>

										<TableCell align="center" sx={{ fontSize: 16 }}>
											<Typography>
												08:00 → <strong>{salidaEjemplo || "-"}</strong>
											</Typography>
										</TableCell>

										<TableCell align="center">
											<Chip
												label={activo === 1 ? "Activo" : "Inactivo"}
												color={activo === 1 ? "success" : "default"}
												variant={activo === 1 ? "filled" : "outlined"}
												onClick={() => handleToggleActivo(convenio)}
												sx={{ cursor: "pointer" }}
											/>
										</TableCell>

										<TableCell align="center">
											<Tooltip title="Editar">
												<IconButton
													color="primary"
													onClick={() => handleEditar(convenio)}
												>
													<EditIcon />
												</IconButton>
											</Tooltip>

											<Tooltip title={activo === 1 ? "Desactivar" : "Activar"}>
												<IconButton
													color={activo === 1 ? "warning" : "success"}
													onClick={() => handleToggleActivo(convenio)}
													disabled={saving}
												>
													<CancelIcon />
												</IconButton>
											</Tooltip>

											<Tooltip title="Eliminar">
												<span>
													<IconButton
														color="error"
														onClick={() => handleEliminar(convenio)}
														disabled={deletingId === id}
													>
														{deletingId === id ? (
															<CircularProgress size={22} />
														) : (
															<DeleteIcon />
														)}
													</IconButton>
												</span>
											</Tooltip>
										</TableCell>
									</TableRow>
								);
							})}
						</TableBody>
					</Table>
				)}
			</Paper>

			<Dialog
				open={formOpen}
				onClose={handleCerrarForm}
				fullWidth
				maxWidth="sm"
			>
				<DialogTitle component="div">
					<Grid
						container
						direction="row"
						justifyContent="space-between"
						alignItems="center"
					>
						<Typography variant="h5">
							{form.id ? "Editar convenio" : "Nuevo convenio"}
						</Typography>

						<IconButton onClick={handleCerrarForm} disabled={saving}>
							<CloseIcon />
						</IconButton>
					</Grid>
				</DialogTitle>

				<DialogContent dividers>
					{formError && (
						<Alert severity="error" sx={{ mb: 2 }}>
							{formError}
						</Alert>
					)}

					<Grid container spacing={2}>
						<Grid size={{ xs: 12 }}>
							<TextField
								fullWidth
								label="Nombre del convenio"
								value={form.nombre}
								onChange={(event) =>
									setForm((current) => ({
										...current,
										nombre: event.target.value,
									}))
								}
								placeholder="Ej: Convenio 8 horas"
							/>
						</Grid>

						<Grid size={{ xs: 12, md: 6 }}>
							<TextField
								fullWidth
								label="Horas convenio"
								value={form.horas_convenio}
								onChange={(event) =>
									setForm((current) => ({
										...current,
										horas_convenio: normalizeDecimalInput(event.target.value),
									}))
								}
								placeholder="8.00"
								InputProps={{
									endAdornment: <InputAdornment position="end">h</InputAdornment>,
								}}
								helperText="Puedes usar 8, 7.5, 6.25, etc."
							/>
						</Grid>

						<Grid size={{ xs: 12, md: 6 }}>
							<FormControl fullWidth>
								<InputLabel id="activo-convenio-label">Estado</InputLabel>
								<Select
									labelId="activo-convenio-label"
									label="Estado"
									value={form.activo}
									onChange={(event) =>
										setForm((current) => ({
											...current,
											activo: Number(event.target.value),
										}))
									}
								>
									<MenuItem value={1}>Activo</MenuItem>
									<MenuItem value={0}>Inactivo</MenuItem>
								</Select>
							</FormControl>
						</Grid>

						<Grid size={{ xs: 12 }}>
							<Alert severity="info">
								Con este convenio, una entrada a las <strong>08:00</strong> tendría salida inicial a las{" "}
								<strong>
									{calcularEjemploSalida(
										"08:00",
										Number(normalizeDecimalInput(form.horas_convenio))
									) || "-"}
								</strong>
								.
							</Alert>
						</Grid>
					</Grid>
				</DialogContent>

				<DialogActions>
					<Button onClick={handleCerrarForm} disabled={saving}>
						Cancelar
					</Button>

					<Button
						variant="contained"
						startIcon={saving ? <CircularProgress size={18} /> : <SaveIcon />}
						onClick={handleGuardar}
						disabled={saving}
					>
						Guardar
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);

	if (isDialog) {
		return (
			<Dialog
				open={open}
				onClose={onClose}
				fullWidth
				maxWidth="lg"
			>
				<DialogTitle component="div">
					<Grid
						container
						direction="row"
						justifyContent="space-between"
						alignItems="center"
					>
						<Typography variant="h4">
							Gestión de convenios
						</Typography>

						<IconButton onClick={onClose}>
							<CloseIcon sx={{ fontSize: 30 }} />
						</IconButton>
					</Grid>
				</DialogTitle>

				<DialogContent dividers>
					{content}
				</DialogContent>
			</Dialog>
		);
	}

	return content;
};